<script setup lang="ts">
import { vIntersectionObserver } from '@vueuse/components'

const props = defineProps<{
  nextLabel?: string
  prevLabel?: string
}>()

const carouselTrack = useTemplateRef('carouselTrack')
const { arrivedState } = useScroll(carouselTrack)
const { $t } = useI18n()

const activeIndex = ref(0)

const nextLabel = computed(() => props.nextLabel || $t('Next'))
const prevLabel = computed(() => props.prevLabel || $t('Previous'))

const scrollToPrev = () => {
  if (carouselTrack.value) {
    const {
      scrollLeft,
      clientWidth,
    } = carouselTrack.value

    carouselTrack.value.scrollTo({
      left: scrollLeft - clientWidth,
      behavior: 'smooth',
    })
  }
}

const scrollToNext = () => {
  if (carouselTrack.value) {
    const {
      scrollLeft,
      clientWidth,
    } = carouselTrack.value

    carouselTrack.value.scrollTo({
      left: scrollLeft + clientWidth,
      behavior: 'smooth',
    })
  }
}

const onMarkerClick = (idx: number) => {
  activeIndex.value = idx
  if (carouselTrack.value) {
    const marker = carouselTrack.value.querySelector(`.ui-carousel__slide:nth-child(${idx + 1})`)
    if (marker) {
      marker.scrollIntoView({ behavior: 'smooth', inline: 'start' })
    }
  }
}

const slidesState = reactive<{
  [key: string]: {
    index: number
    isVisible: boolean
  }
}>({})

function onIntersectionObserver([entry]: IntersectionObserverEntry[]) {
  if (!isNaN(entry?.target?.dataset?.index)) {
    slidesState[entry.target.dataset.index] = {
      index: +(entry.target.dataset.index),
      isVisible: entry.isIntersecting,
    }
  }
}

let timer: ReturnType<typeof setTimeout> | null = null
watch(slidesState, () => {
  if (timer) {
    clearTimeout(timer)
  }

  timer = setTimeout(() => {
    // Fix for center mode
    activeIndex.value = Object.values(slidesState).filter(slide => slide.isVisible)?.sort((a, b) => a.index - b.index)?.at(0)?.index || 0
  }, 250)
})

defineExpose({
  scrollToPrev,
  scrollToNext,
  arrivedState,
})
</script>

<template>
  <div class="ui-carousel">
    <button
      :aria-label="prevLabel"
      class="ui-carousel__button ui-carousel__button--prev"
      :class="{
        'ui-carousel__button--disabled': arrivedState.left,
      }"
      @click="scrollToPrev"
    />

    <button
      :aria-label="nextLabel"
      class="ui-carousel__button ui-carousel__button--next"
      :class="{
        'ui-carousel__button--disabled': arrivedState.right,
      }"
      @click="scrollToNext"
    />

    <div
      ref="carouselTrack"
      class="ui-carousel__track"
      :data-next-label="nextLabel"
      :data-prev-label="prevLabel"
    >
      <div
        v-for="(_slot, name, index) in $slots"
        :key="name"
        v-intersection-observer="[onIntersectionObserver, { root: carouselTrack }]"
        class="ui-carousel__slide"
        :data-index="index"
        :style="`--index: ${index}`"
      >
        <slot :name="name" />
      </div>
    </div>

    <div class="ui-carousel__pagination">
      <button
        v-for="(_slot, name, index) in $slots"
        :key="name"
        type="button"
        class="ui-carousel__marker"
        :class="{
          'ui-carousel__marker--active': activeIndex === index,
        }"
        @click="onMarkerClick(index)"
      />
    </div>
  </div>
</template>

<style src="~layers/app/components/Ui/Carousel.css" />
